export var Role;
(function (Role) {
    Role["SUPER_HR_ADMIN"] = "SUPER_HR_ADMIN";
    Role["SR_HR_MANAGER"] = "SR_HR_MANAGER";
    Role["HR_MANAGER"] = "HR_MANAGER";
    Role["HR_EXECUTIVE"] = "HR_EXECUTIVE";
    Role["PAYROLL_ADMIN"] = "PAYROLL_ADMIN";
    Role["FINANCE"] = "FINANCE";
    Role["MANAGER"] = "MANAGER";
    Role["SR_MANAGER"] = "SR_MANAGER";
    Role["DIRECTOR"] = "DIRECTOR";
    Role["VP"] = "VP";
    Role["C_SUITE"] = "C_SUITE";
    Role["EMPLOYEE"] = "EMPLOYEE";
    Role["CONTRACTOR"] = "CONTRACTOR";
    Role["INTERN"] = "INTERN";
    Role["READONLY_AUDITOR"] = "READONLY_AUDITOR";
    // Net-new values from the business-approved 12-role taxonomy (Phase A1).
    // SR_HR_MANAGER, HR_MANAGER, HR_EXECUTIVE, DIRECTOR, VP, C_SUITE, EMPLOYEE,
    // CONTRACTOR, INTERN, READONLY_AUDITOR are retired by Phase A7 (separate,
    // later, irreversible change) once A2's data migration has been live and
    // observed — do not remove them here.
    Role["TEAM_LEAD"] = "TEAM_LEAD";
    Role["REGIONAL_HR"] = "REGIONAL_HR";
    Role["CORPORATE_HR"] = "CORPORATE_HR";
    Role["REGIONAL_ADMIN"] = "REGIONAL_ADMIN";
    Role["CORPORATE_ADMIN"] = "CORPORATE_ADMIN";
    Role["REGIONAL_IT"] = "REGIONAL_IT";
    Role["CORPORATE_IT"] = "CORPORATE_IT";
    Role["ENGINEER"] = "ENGINEER";
})(Role || (Role = {}));
// Phase A5 (see access-control plan): canonical role-groupings, replacing
// ~50 independently hand-typed copies across apps/api/src and apps/web/src.
// Values below are the new-taxonomy equivalents of each old-taxonomy group —
// each old role is translated per Phase A0's mapping table
// (SR_HR_MANAGER->CORPORATE_HR, HR_MANAGER/HR_EXECUTIVE->REGIONAL_HR,
// MANAGER->TEAM_LEAD, SR_MANAGER->MANAGER, DIRECTOR/VP/C_SUITE->SR_MANAGER,
// EMPLOYEE/CONTRACTOR/INTERN->ENGINEER, READONLY_AUDITOR->SUPER_HR_ADMIN),
// then de-duplicated. Import these instead of re-declaring a local Set.
export const SENSITIVE_ROLES = new Set([
    Role.SUPER_HR_ADMIN,
    Role.PAYROLL_ADMIN,
    Role.FINANCE,
    Role.CORPORATE_HR,
]);
export const HR_ROLES = new Set([
    Role.SUPER_HR_ADMIN,
    Role.CORPORATE_HR,
    Role.REGIONAL_HR,
]);
// Old: {SUPER_HR_ADMIN, SR_HR_MANAGER}. Also replaces the several
// differently-named-but-identical-valued sets found across the codebase:
// CLI_ROLES (logs.route.ts), SUPER_HR (email-templates.route.ts,
// leave-policy.route.ts, EmailTemplatesAdminPage.tsx, LeaveAdminPage.tsx).
export const ADMIN_ROLES = new Set([
    Role.SUPER_HR_ADMIN,
    Role.CORPORATE_HR,
]);
// "Manager tier or above." Old: {MANAGER, SR_MANAGER, DIRECTOR, VP, C_SUITE}.
export const MGR_ROLES = new Set([
    Role.TEAM_LEAD,
    Role.MANAGER,
    Role.SR_MANAGER,
]);
// Old: {DIRECTOR, VP, C_SUITE} — all three collapsed into one new value.
export const SENIOR_ROLES = new Set([Role.SR_MANAGER]);
// Old: {FINANCE, PAYROLL_ADMIN, SUPER_HR_ADMIN} — none of these three
// change value in Phase A, so this set is unchanged.
export const FINANCE_ROLES = new Set([
    Role.FINANCE,
    Role.PAYROLL_ADMIN,
    Role.SUPER_HR_ADMIN,
]);
// Old: {SUPER_HR_ADMIN, SR_HR_MANAGER, PAYROLL_ADMIN, FINANCE}. Kept as two
// separate exports (PAYROLL_ROLES, SALARY_ROLES) despite identical values —
// they gate conceptually distinct things (payroll module access vs.
// salary-field visibility) and may diverge independently later.
export const PAYROLL_ROLES = new Set([
    Role.SUPER_HR_ADMIN,
    Role.CORPORATE_HR,
    Role.PAYROLL_ADMIN,
    Role.FINANCE,
]);
export const SALARY_ROLES = new Set([
    Role.SUPER_HR_ADMIN,
    Role.CORPORATE_HR,
    Role.PAYROLL_ADMIN,
    Role.FINANCE,
]);
// Finance verification of compensation revisions and compensation rules (approve / reject).
// Finance only: HR (incl. Super HR Admin) prepares and submits, Finance verifies. Maker-checker
// (no approving your own submission) is enforced in compensation.service, not by this set.
export const COMP_VERIFY_ROLES = new Set([
    Role.FINANCE,
]);
// Expense analytics dashboard — the Expenses page's read-only "Report" tab and its
// GET /api/expenses/analytics endpoint. Deliberately narrower than FINANCE_ROLES:
// the requirement is "super HR and finance only", so PAYROLL_ADMIN (which is in
// FINANCE_ROLES) is intentionally excluded here.
export const EXPENSE_ANALYTICS_ROLES = new Set([
    Role.SUPER_HR_ADMIN,
    Role.FINANCE,
]);
// Petty Cash Book (Expenses page tab, /api/petty-cash). Admins keep the book; Finance
// (plus Super HR Admin) sees every admin's book, approves entries and allocates budget.
// Corporate-vs-regional visibility lives in modules/petty-cash/petty-cash.scope.ts.
export const PETTY_CASH_FINANCE_ROLES = new Set([
    Role.FINANCE,
    Role.SUPER_HR_ADMIN,
]);
export const PETTY_CASH_ADMIN_ROLES = new Set([
    Role.REGIONAL_ADMIN,
    Role.CORPORATE_ADMIN,
]);
export const PETTY_CASH_ROLES = new Set([
    ...PETTY_CASH_FINANCE_ROLES,
    ...PETTY_CASH_ADMIN_ROLES,
]);
// Old: {SUPER_HR_ADMIN, SR_HR_MANAGER, HR_MANAGER, READONLY_AUDITOR}.
export const AUDIT_ROLES = new Set([
    Role.SUPER_HR_ADMIN,
    Role.CORPORATE_HR,
    Role.REGIONAL_HR,
]);
// Old (succession.route.ts): {SUPER_HR_ADMIN, SR_HR_MANAGER, HR_MANAGER,
// DIRECTOR, VP, C_SUITE}.
export const LEAD_ROLES = new Set([
    Role.SUPER_HR_ADMIN,
    Role.CORPORATE_HR,
    Role.REGIONAL_HR,
    Role.SR_MANAGER,
]);
// Asset/IT-ticket module access (apps/api/src/modules/assets/assets.route.ts,
// apps/web/src/pages/AssetsPage.tsx). Replaces the old, misleadingly-named
// `IT_STAFF` (old value {SUPER_HR_ADMIN, SR_HR_MANAGER, HR_MANAGER} — no IT
// role existed yet, so HR-tier roles stood in for it). Phase A5 closes gap
// #7 from the plan: REGIONAL_IT/CORPORATE_IT are added ADDITIVELY (existing
// HR-tier access is preserved, not revoked, to avoid a regression) alongside
// the real new IT roles.
export const ASSET_ROLES = new Set([
    Role.SUPER_HR_ADMIN,
    Role.CORPORATE_HR,
    Role.REGIONAL_HR,
    Role.REGIONAL_IT,
    Role.CORPORATE_IT,
]);
// Old: {SUPER_HR_ADMIN} only. Extended additively to include CORPORATE_IT
// (the senior IT tier) now that a real IT role exists, mirroring how
// CORPORATE_HR/CORPORATE_ADMIN are the senior tier elsewhere.
export const ASSET_ADMIN_ROLES = new Set([
    Role.SUPER_HR_ADMIN,
    Role.CORPORATE_IT,
]);
// HRMS-0501. Viewing-only grant, deliberately kept out of ASSET_ROLES/FINANCE_ROLES —
// those gate real write authority across the assets module; SR_MANAGER gets none of it.
// Consumed only by asset-dashboards.route.ts's Executive endpoint and ReportsPage's
// Executive tab gate. Safe only because that endpoint is aggregate-only (no row-level
// data). Any third use of this set is a review red flag.
export const ASSET_DASHBOARD_ROLES = new Set([
    Role.SR_MANAGER,
]);
// The 13 roles a person can actually be assigned going forward (role-picker
// dropdowns, role-validity checks). Deliberately narrower than `Object.values(Role)`
// — the enum still contains the old, soon-to-be-retired values (valid for
// existing rows until Phase A7), but nothing should let a user newly select one.
export const ASSIGNABLE_ROLES = [
    Role.SUPER_HR_ADMIN,
    Role.ENGINEER,
    Role.TEAM_LEAD,
    Role.MANAGER,
    Role.SR_MANAGER,
    Role.REGIONAL_HR,
    Role.CORPORATE_HR,
    Role.REGIONAL_ADMIN,
    Role.CORPORATE_ADMIN,
    Role.REGIONAL_IT,
    Role.CORPORATE_IT,
    Role.PAYROLL_ADMIN,
    Role.FINANCE,
];
//# sourceMappingURL=roles.js.map